import { Link } from 'react-router-dom';
import { AlertOctagon, Clock, CalendarClock, CheckCircle2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import type {
  ExceptionKind,
  ExceptionRow,
  Exceptions,
  Severity,
} from '@/api/services/dashboard.api';

/**
 * Band one: what needs a decision today.
 *
 * The panel this replaces listed three counts — overdue work orders, open
 * discrepancies, pending receipts — with no link on any of them, so the reader
 * learned a number and then had to go and find the records themselves. Every
 * row here opens the thing it is about.
 *
 * Ordering is the server's, and it is worst-first by severity before age: a
 * lapsed certificate that stops an asset working outranks a rental thirty days
 * late, because one halts work and the other accrues an invoice.
 */

const SEVERITY: Record<
  Severity,
  { label: string; icon: typeof AlertOctagon; dot: string; chip: string }
> = {
  BREACHED: {
    label: 'Breached',
    icon: AlertOctagon,
    dot: 'bg-status-destructive',
    chip: 'bg-status-destructive-subtle text-status-destructive-foreground',
  },
  ACT_NOW: {
    label: 'Overdue',
    icon: Clock,
    dot: 'bg-status-warning',
    chip: 'bg-status-warning-subtle text-status-warning-foreground',
  },
  PLAN: {
    label: 'To plan',
    icon: CalendarClock,
    dot: 'bg-status-info',
    chip: 'bg-status-info-subtle text-status-info-foreground',
  },
};

/**
 * Kinds for which a due date is a thing that exists. A blocked job has no
 * return date to have been left unset, so labelling it "no date set" would
 * invent an omission nobody made.
 */
const DATED: ReadonlySet<ExceptionKind> = new Set<ExceptionKind>([
  'ASSET_OVERDUE',
  'RENTAL_OVERDUE',
  'RENTAL_NO_DUE_DATE',
  'WORK_ORDER_OVERDUE',
  'SHIPMENT_DELAYED',
  'PERMIT_BREACHED',
]);

/** How late, in the words someone would say out loud. */
function lateness(days: number | null): string | null {
  if (days == null) return null;
  if (days >= 60) return `${Math.floor(days / 30)} months over`;
  if (days >= 14) return `${Math.floor(days / 7)} weeks over`;
  return `${days} ${days === 1 ? 'day' : 'days'} over`;
}

function Row({ row }: { row: ExceptionRow }) {
  const s = SEVERITY[row.severity];
  const late = lateness(row.daysOverdue);
  return (
    <Link
      to={row.href}
      className="group flex items-start gap-3 rounded-lg border border-transparent px-3 py-2.5 transition-colors hover:border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className={cn('mt-1.5 h-2 w-2 flex-none rounded-full', s.dot)} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{row.title}</span>
        <span className="block truncate text-xs text-muted-foreground">{row.detail}</span>
      </span>
      {late ? (
        <span className="flex-none whitespace-nowrap text-xs font-medium tabular-nums text-muted-foreground">
          {late}
        </span>
      ) : DATED.has(row.kind) ? (
        // A date was expected here and is missing, which is its own problem —
        // nothing will chase a hire with no return date.
        <span className="flex-none whitespace-nowrap text-xs text-status-warning-foreground">
          no date set
        </span>
      ) : null}
      <ChevronRight className="mt-0.5 h-4 w-4 flex-none text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

export function ExceptionBand({ data, loading }: { data: Exceptions | null; loading: boolean }) {
  if (loading) {
    return (
      <div className={cn(PANEL, 'h-full p-5')}>
        <div className="h-5 w-64 animate-pulse rounded bg-muted" />
        <div className="mt-4 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-11 animate-pulse rounded-lg bg-muted/60" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  // A clear board is a result, not an empty state. It gets the same panel and
  // says so in a sentence.
  if (data.total === 0) {
    return (
      <div className={cn(PANEL, 'flex h-full items-center gap-3 p-5')}>
        <CheckCircle2 className="h-5 w-5 flex-none text-status-success" />
        <div>
          <p className="text-sm font-medium text-foreground">Nothing outstanding</p>
          <p className="text-xs text-muted-foreground">
            No breach, no overdue return, and no job blocked from mobilising.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(PANEL, 'flex h-full flex-col')}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">Needs a decision</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{data.statement}</p>
        </div>
        <div className="flex flex-none items-center gap-2">
          {(['BREACHED', 'ACT_NOW', 'PLAN'] as const)
            .filter((k) => data.counts[k] > 0)
            .map((k) => {
              const s = SEVERITY[k];
              const Icon = s.icon;
              return (
                <span
                  key={k}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                    s.chip
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {data.counts[k]} {s.label.toLowerCase()}
                </span>
              );
            })}
        </div>
      </div>

      {/* The list scrolls inside a fixed height rather than growing the page.
          Nineteen rows pushed everything below the fold out of reach, and the
          alternative — truncating at eight — meant a worklist that hid its own
          tail and read as complete when it was not. Scrolling shows all of
          them and costs the page nothing. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {data.rows.map((row, i) => (
          <Row key={`${row.kind}-${row.href}-${i}`} row={row} />
        ))}
      </div>
    </div>
  );
}
